import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Link } from "react-router-dom";

import { CampaignDisplayStatus } from "@signalist/shared-types";

import { Table } from "@/components/Table/Table";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Toggle } from "@/components/Toggle/Toggle";

import { CampaignValidationState } from "@/pages/Private/pages/List/components/CampaignOverview/utils/useCampaignValidation";

import { blockingCampaignNames } from "./campaignsDisableBlockers";
import { CampaignsDisableBlockedModal } from "./CampaignsDisableBlockedModal";
import { CampaignsEmptyState } from "./CampaignsEmptyState";
import {
	buildCampaignColumns,
	buildFooterRow,
	computeSummary,
	type CampaignStats,
} from "./CampaignColumns";

import { OutreachSection } from "./OutreachSection";

import { CampaignRoutingSummary } from "../../../components/CampaignRouting/CampaignRoutingSummary";
import { useCampaignRoutingEditor } from "../../../components/CampaignRouting/useCampaignRoutingEditor";

import type { CopywritingCampaign } from "../ListEditCopywriting";

/** Minimal slice of the list-query data the section needs. */
interface CampaignsSectionListData {
	id: number;
	name?: string | null;
	campaignConfig?: Record<string, CopywritingCampaign> | null;
	campaignStatuses?: Record<string, { status?: CampaignDisplayStatus; locked?: boolean }> | null;
	event?: { enabled?: boolean } | null;
	/** Whether auto-assign enrolls routed leads: the routing editor says what saving means. */
	autoAssignToCampaign?: boolean | null;
}

interface CampaignsSectionProps {
	listData: CampaignsSectionListData;
	/** Live per-campaign validity + reasons (PREC-3952), keyed by campaign uuid. */
	campaignValidation?: Record<string, CampaignValidationState>;
	statsByCampaign: Record<string, CampaignStats>;
	/** Whether the stats endpoint has answered (PREC-4291: a missing stat is then a zero). */
	statsLoaded?: boolean;
	/** Whether campaigns are switched on for this list — off collapses the section to the toggle. */
	campaignsEnabled: boolean;
	/** Persist the toggle. Only called once the disable guard has passed (PREC-3687 O4). */
	onCampaignsEnabledChange: (enabled: boolean) => void;
	isTogglingCampaignStatus: boolean;
	onToggleCampaignRunStatus: (campaignUuid: string, status: CampaignDisplayStatus) => void;
	onSetAsDefault: (campaign: CopywritingCampaign) => void;
	onCloneCampaign: (campaign: CopywritingCampaign) => void;
	onDeleteCampaign: (campaign: CopywritingCampaign) => void;
	/** Start the create-campaign flow (may ask Event vs Regular first). */
	onCreateCampaign: () => void;
	/** Open the "copy campaigns from another list" modal. */
	onCopyFromList: () => void;
	/** Bulk-apply the event end date as every campaign's stop date. */
	onApplyEventStopDate: () => void;
	/** Skip campaigns and continue to the list settings step (empty state only). */
	onSkip: () => void;
	/** Open the campaign builder for a table row. */
	onRowClick: (campaignUuid: string) => void;
}

/**
 * The Campaigns section of the Outreach step (PREC-3687): the campaign table with its footer totals
 * and analytics link, the header actions, and the empty state for a list without campaigns.
 */
export const CampaignsSection: FunctionComponent<CampaignsSectionProps> = ({
	listData,
	campaignValidation,
	statsByCampaign,
	statsLoaded,
	campaignsEnabled,
	onCampaignsEnabledChange,
	isTogglingCampaignStatus,
	onToggleCampaignRunStatus,
	onSetAsDefault,
	onCloneCampaign,
	onDeleteCampaign,
	onCreateCampaign,
	onCopyFromList,
	onApplyEventStopDate,
	onSkip,
	onRowClick,
}) => {
	const { t } = useTranslation();
	const tc = (key: string) => t(`list.outreach.campaigns.${key}`);

	const [blockedNames, setBlockedNames] = useState<string[]>([]);

	const campaignConfig = listData.campaignConfig;

	const routing = useCampaignRoutingEditor({
		listId: listData.id,
		campaignConfig,
		campaignStatuses: listData.campaignStatuses,
		autoAssignEnabled: !!listData.autoAssignToCampaign,
	});

	const columns = buildCampaignColumns({
		data: listData,
		campaignValidation,
		statsByCampaign,
		statsLoaded,
		isTogglingCampaignStatus,
		onToggleCampaignRunStatus,
		onSetAsDefault,
		onCloneCampaign,
		onDeleteCampaign,
	});

	const tableData = useMemo(() => {
		if (!campaignConfig) {
			return [];
		}

		const { name: _name, ...rest } = campaignConfig;

		return Object.keys(rest).map((key) => ({
			...rest[key],
			id: key,
		}));
	}, [campaignConfig]);

	const summary = useMemo(
		() => computeSummary(tableData, statsByCampaign, listData.campaignStatuses),
		[tableData, statsByCampaign, listData.campaignStatuses]
	);

	const footerRow = useMemo(() => buildFooterRow(columns, summary), [columns, summary]);

	const hasCampaigns = !!campaignConfig && tableData.length > 0;

	/**
	 * Switching campaigns off is blocked while the list is still sending (PREC-3687 O4) — the user
	 * has to pause those campaigns first. Turning them back **on** is never blocked.
	 */
	const handleToggleCampaignsEnabled = () => {
		if (campaignsEnabled) {
			const blocking = blockingCampaignNames(listData.campaignStatuses, campaignConfig);

			if (blocking.length > 0) {
				setBlockedNames(blocking);

				return;
			}
		}

		onCampaignsEnabledChange(!campaignsEnabled);
	};

	return (
		<>
			<OutreachSection
				headerActions={
					campaignsEnabled && hasCampaigns ? (
						<div className="flex flex-row items-center gap-3">
							{listData.event?.enabled && campaignConfig ? (
								<Button
									color={ButtonColor.ACTION_SECONDARY}
									size={ButtonSize.XS}
									title={tc("applyEventEndDate")}
									onClick={onApplyEventStopDate}
								/>
							) : null}
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								size={ButtonSize.XS}
								title={tc("copyFromList")}
								onClick={onCopyFromList}
							/>
							<Button
								color={ButtonColor.ACTION}
								size={ButtonSize.XS}
								title={tc("createNew")}
								onClick={onCreateCampaign}
							/>
						</div>
					) : null
				}
				subtitle={tc("subtitle")}
				title={tc("title")}
			>
				<div className="mb-4 flex items-start gap-3">
					<Toggle
						className="mt-0.5"
						isChecked={campaignsEnabled}
						testId="campaigns-enabled-toggle"
						onChange={handleToggleCampaignsEnabled}
					/>
					<div className="min-w-0">
						<p className="text-sm font-semibold text-text">{tc("enable.label")}</p>
						<p className="mt-0.5 text-xs leading-5 text-text-meta">{tc("enable.description")}</p>
					</div>
				</div>

				{/* Off keeps every campaign untouched — the table, the empty state and the header actions
				    simply stop rendering until it is switched back on (PREC-3687 §4.2 / D5). */}
				{!campaignsEnabled ? null : hasCampaigns ? (
					<div className="rounded-xl">
						<div className="w-full">
							<Table
								columns={columns}
								data={tableData}
								footerRow={footerRow}
								handleRowClickWithStringId={onRowClick}
								hideFooter={true}
								hidePagination={true}
							/>
						</div>
						<div className="mt-3 flex justify-end">
							<Link
								className="text-sm font-medium text-text-meta underline underline-offset-2 hover:text-brand-light"
								to={`/app/analytics?listId=${listData.id}`}
							>
								{tc("viewAnalytics")}
							</Link>
						</div>
						<CampaignRoutingSummary
							campaigns={routing.campaigns}
							failed={routing.failed}
							settings={routing.settings}
							onEdit={routing.open}
						/>
					</div>
				) : (
					<CampaignsEmptyState
						onCopy={onCopyFromList}
						onCreate={onCreateCampaign}
						onSkip={onSkip}
					/>
				)}
			</OutreachSection>
			{/* Mounted only while it has something to say — the shared Modal is `keepMounted`, so an
			    always-mounted instance would leave its copy in the DOM. */}
			{routing.editor}
			{blockedNames.length > 0 && (
				<CampaignsDisableBlockedModal
					campaignNames={blockedNames}
					isOpened={true}
					onClose={() => setBlockedNames([])}
				/>
			)}
		</>
	);
};
